import clsx from 'clsx';
import { useRef } from 'react';
import { graphql, Script } from 'gatsby';
import { COUNTRY, locales } from '~globals';
import { BaseLink } from '~components/BaseLink';

import * as styles from './ComponentTrustpilot.module.css';

import type { ContentfulComponentTrustpilot, TrustpilotTemplate } from './types';

const WIDGET_SRC = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const BUSINESSUNIT_ID = '64576da60193dd434c83a77d';

declare global {
  interface Window {
    Trustpilot?: { loadFromElement: (element?: HTMLElement, forceReload?: boolean) => void };
  }
}

type ComponentTrustpilotProps = {
  trustpilot?: Partial<ContentfulComponentTrustpilot>;
  className?: string;
};

export const ComponentTrustpilot: React.FC<ComponentTrustpilotProps> = ({ trustpilot, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const template = getTemplate(trustpilot?.template ?? 'Micro TrustScore');

  return (
    <>
      <Script
        src={WIDGET_SRC}
        onLoad={() => {
          if (ref.current) window.Trustpilot?.loadFromElement(ref.current, true);
        }}
      />
      <div
        ref={ref}
        className={clsx(styles.wrapper, styles[COUNTRY], template.className, className)}
        data-locale={locales[COUNTRY]}
        data-businessunit-id={BUSINESSUNIT_ID}
        data-template-id={template.id}
        data-theme={(trustpilot?.theme ?? 'light').toLowerCase()}
      >
        <BaseLink href={`https://${COUNTRY}.trustpilot.com/review/insify.com`} target='_blank' rel='noopener'>
          Trustpilot
        </BaseLink>
      </div>
    </>
  );
};

const getTemplate = (template: TrustpilotTemplate): { id: string; className: string } => {
  switch (template) {
    case 'Mini':
      return { id: '53aa8807dec7e10d38f59f32', className: styles.mini };
    case 'Micro Star':
      return { id: '5419b732fbfb950b10de65e5', className: styles.microStar };
    case 'Micro TrustScore':
    default:
      return { id: '5419b637fa0340045cd0c936', className: styles.microTrustScore };
  }
};

export const query = graphql`
  fragment ComponentTrustpilot on ContentfulComponentTrustpilot {
    __typename
    template
    theme
  }
`;
